import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { currentUser, User } from '../../../../../core/auth';
import { CompanyService } from '../../../../../core/auth/_services/company.service';
import { LotService, ProductService, WorkOrderService } from '../../../../../core/plant-configuration';
import { AppState } from '../../../../../core/reducers';
import { getApiErrorMessage } from '../../../../../core/util/api-error.util';

@Component({
  selector: 'kt-work-order-edit',
  templateUrl: './work-order-edit.component.html',
  styleUrls: ['./work-order-edit.component.scss']
})
export class WorkOrderEditComponent implements OnInit, OnDestroy {
  itemObs = new Subject<any>();
  editObs = new Subject<number>();
  addObs = new Subject<number>();
  itemForm = this.formBuilder.group({
    work_order_number: ['', [Validators.required, Validators.maxLength(100)]],
    status_id: [null, Validators.required], planned_start: [null], planned_end: [null],
    source_type: [{value: 'MANUAL', disabled: true}],
    source_reference: ['', Validators.maxLength(255)],
    creator_label: [{value: '', disabled: true}], comment: [''],
    products: new FormArray([]), lots: new FormArray([])
  });
  stepList: any[] = [
    {icon: 'flaticon-settings-1', title: 'Datos de la orden de trabajo', subTitle: 'Define la planificación de la orden', form: [
      [{name: 'work_order_number', label: 'N° de orden de trabajo', type: 'text', instructions: 'Ingrese el número de la orden de trabajo'},
       {name: 'status_id', label: 'Estado', type: 'list', instructions: 'Seleccione el estado de la orden', optionList: []}],
      [{name: 'planned_start', label: 'Inicio planificado', type: 'datetime-local', instructions: 'Seleccione el inicio planificado'},
       {name: 'planned_end', label: 'Fin planificado', type: 'datetime-local', instructions: 'Seleccione el fin planificado'}],
      [{name: 'source_type', label: 'Fuente', type: 'text', instructions: 'La fuente se asigna automáticamente'},
       {name: 'creator_label', label: 'Usuario que crea', type: 'text', instructions: 'Usuario autenticado que realiza la operación'}],
      [{name: 'source_reference', label: 'Referencia de origen', type: 'text', instructions: 'Referencia opcional del sistema de origen'}],
      [{name: 'comment', label: 'Comentarios', type: 'textarea'}],
      [{name: 'products', label: 'Productos', listTitle: 'Productos planificados', type: 'table',
        uniqueColumn: 'product_id', minimumRows: 1, requiredColumns: ['product_id', 'planned_units'],
        columnList: [
          {name: 'product_id', label: 'Producto', type: 'list', optionList: []},
          {name: 'planned_units', label: 'Cantidad planificada', type: 'number', min: 0}
        ], selected: []}]
    ]},
    {icon: 'flaticon-list-2', title: 'Lotes', subTitle: 'Define los lotes de la orden', form: [[{
      name: 'lots', label: 'Lotes', listTitle: 'Lotes asignados', type: 'table',
      columnList: [
        {name: 'lot_number', label: 'N° de lote', type: 'text'},
        {name: 'product_id', label: 'Producto', type: 'list', optionList: []},
        {name: 'status_id', label: 'Estado', type: 'list', optionList: []},
        {name: 'source_reference', label: 'Referencia', type: 'text'}
      ], selected: [], requiredColumns: ['lot_number', 'product_id', 'status_id']
    }]]}
  ];
  data: any = {products: [], statuses: []};
  selectedCompanyId: number = null;
  user: User;
  creatorId: number;
  creatorName = '';
  startStep = 1;
  private loadedLotIds: number[] = [];
  private originalOrder: any = null;
  private subscriptions: Subscription[] = [];

  constructor(private workOrderService: WorkOrderService, private lotService: LotService,
    private productService: ProductService, private companyService: CompanyService,
    private store: Store<AppState>, private formBuilder: FormBuilder,
    private snackBar: MatSnackBar, private cdr: ChangeDetectorRef,
    private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.startStep = this.route.snapshot.queryParamMap.get('step') === 'lots' ? 2 : 1;
    this.subscriptions.push((this.itemForm.get('products') as FormArray).valueChanges.subscribe(() => {
      this.configureLotProductOptions();
    }));
    this.subscriptions.push(this.store.pipe(select(currentUser)).subscribe(user => {
      this.user = user;
      if (user) {
        this.creatorId = user.id;
        this.creatorName = user.fullname || user.username || user.login;
        this.itemForm.get('creator_label').setValue(this.getCreatorLabel(), {emitEvent: false});
      }
    }));
    // El superusuario usa la empresa seleccionada; los demás, su propia empresa.
    this.subscriptions.push(combineLatest([
      this.store.pipe(select(currentUser), filter(user => !!user && Number(user.id) > 0)),
      this.companyService.companyLoaded$
    ]).pipe(
      map(([user, loaded]) => {
        if (Number(user.group_id) !== 4) return Number(user.company_id);
        const company = loaded ? this.companyService.getCurrentCompany() : null;
        return company && Number(company.id);
      }),
      filter(companyId => Number.isInteger(companyId) && companyId > 0), distinctUntilChanged(),
      switchMap(companyId => {
        const changed = this.selectedCompanyId !== null && this.selectedCompanyId !== companyId;
        this.selectedCompanyId = companyId;
        if (changed) this.clearCompanyData();
        return forkJoin([this.productService.getAll({company_id: companyId}), this.workOrderService.getAll()]);
      })
    ).subscribe(([products, orders]) => {
      this.data.products = products || [];
      this.collectStatuses(orders || []);
      this.configureOptions();
      this.syncProductDisplay();
      this.syncLotDisplay();
      this.cdr.detectChanges();
    }, error => {
      console.error('Error al cargar catálogos de órdenes de trabajo', error);
      this.snackBar.open('No fue posible cargar los datos del formulario.', 'Cerrar', {duration: 5000});
    }));
  }

  ngOnDestroy(): void { this.subscriptions.forEach(subscription => subscription.unsubscribe()); }

  getInput(name: string): any {
    for (const step of this.stepList) for (const row of step.form) {
      const input = row.find(item => item.name === name);
      if (input) return input;
    }
    return null;
  }

  getItem(id: number): void {
    this.subscriptions.push(this.workOrderService.getById(id).subscribe(order => {
      this.prepareLoadedOrder(order);
      this.originalOrder = this.clone(order);
      this.itemObs.next(order);
    }, error => {
      console.error('Error al cargar la orden de trabajo', error);
      this.snackBar.open('No fue posible cargar la orden de trabajo.', 'Cerrar', {duration: 5000});
    }));
  }

  addItem(item: any): void { this.persist(item, false); }
  editItem(item: any): void { this.persist(item, true); }

  resetItem(): void {
    if (this.originalOrder) {
      const order = this.clone(this.originalOrder);
      this.prepareLoadedOrder(order);
      this.itemForm.patchValue(order);
    } else {
      this.itemForm.reset({source_type: 'MANUAL', creator_label: this.getCreatorLabel()});
      this.clearRows('products');
      this.clearRows('lots');
      this.getInput('products').selected = [];
      this.getInput('lots').selected = [];
      const statuses = this.getInput('status_id').optionList || [];
      if (statuses.length) this.itemForm.get('status_id').setValue(statuses[0].value);
    }
    this.itemForm.markAsPristine();
    this.itemForm.markAsUntouched();
    this.cdr.detectChanges();
  }

  getProductName(id: any): string {
    const product = this.data.products.find(item => String(item.id) === String(id));
    return product ? product.name : 'Sin producto';
  }
  getStatusName(id: any): string {
    const status = this.data.statuses.find(item => String(item.id) === String(id));
    return status ? status.name : 'Sin estado';
  }
  getCreatorLabel(): string {
    return this.creatorId ? (this.creatorName || 'Usuario') + ' (ID ' + this.creatorId + ')' : 'Cargando usuario...';
  }

  private persist(item: any, editing: boolean): void {
    if (!this.validate(item)) { editing ? this.editObs.next(-1) : this.addObs.next(-1); return; }
    const payload: any = {
      id: editing ? item.id : null, company_id: this.selectedCompanyId,
      work_order_number: String(item.work_order_number).trim(), status_id: Number(item.status_id),
      planned_start: item.planned_start || null, planned_end: item.planned_end || null,
      source_type: 'MANUAL', source_reference: item.source_reference || null, comment: item.comment || null,
      products: (item.products || []).map(product => ({
        product_id: Number(product.product_id),
        planned_units: product.planned_units === null || product.planned_units === ''
          ? null : Number(product.planned_units)
      }))
    };
    const save$ = editing ? this.workOrderService.edit(payload).pipe(map(() => item.id)) : this.workOrderService.add(payload);
    this.subscriptions.push(save$.pipe(switchMap(id => this.persistLots(id, item.lots || [], payload))).subscribe(id => {
      editing ? this.editObs.next(id) : this.addObs.next(id);
    }, error => {
      console.error('Error al guardar la orden de trabajo', error);
      this.snackBar.open(getApiErrorMessage(error, 'No fue posible guardar la orden de trabajo.'), 'Cerrar', {duration: 5000});
      editing ? this.editObs.next(-1) : this.addObs.next(-1);
    }));
  }

  private persistLots(orderId: number, lots: any[], order: any): Observable<number> {
    const operations: Observable<any>[] = lots.map(lot => {
      const payload = Object.assign({}, lot, {work_order_id: orderId, company_id: this.selectedCompanyId,
        product_id: Number(lot.product_id), status_id: lot.status_id || order.status_id,
        source_type: 'MANUAL'});
      return lot.id ? this.lotService.edit(payload) : this.lotService.add(payload);
    });
    const currentIds = lots.filter(lot => lot.id).map(lot => Number(lot.id));
    this.loadedLotIds.filter(id => currentIds.indexOf(id) < 0)
      .forEach(id => operations.push(this.lotService.delete({id} as any)));
    return (operations.length ? forkJoin(operations) : of([])).pipe(map(() => orderId));
  }

  private validate(item: any): boolean {
    const products = item.products || [];
    if (!this.selectedCompanyId || !products.length) {
      this.snackBar.open('La orden de trabajo debe incluir al menos un producto.', 'Cerrar', {duration: 4000});
      return false;
    }
    const ids = products.map(product => String(product.product_id));
    if (new Set(ids).size !== ids.length) {
      this.snackBar.open('No se puede agregar el mismo producto más de una vez.', 'Cerrar', {duration: 4000}); return false;
    }
    if (products.some(row => !this.data.products.some(product => String(product.id) === String(row.product_id)))) {
      this.snackBar.open('Uno de los productos no pertenece a la empresa seleccionada.', 'Cerrar', {duration: 4000}); return false;
    }
    if (products.some(row => row.planned_units === null || row.planned_units === '' || Number(row.planned_units) < 0)) {
      this.snackBar.open('Todas las cantidades planificadas deben ser iguales o superiores a cero.', 'Cerrar', {duration: 4000}); return false;
    }
    if (item.planned_start && item.planned_end && new Date(item.planned_end).getTime() < new Date(item.planned_start).getTime()) {
      this.snackBar.open('El fin planificado no puede ser anterior al inicio.', 'Cerrar', {duration: 4000}); return false;
    }
    if ((item.lots || []).some(lot => !lot.lot_number || !lot.status_id)) {
      this.snackBar.open('Completa el número y estado de todos los lotes.', 'Cerrar', {duration: 4000}); return false;
    }
    if ((item.lots || []).some(lot => ids.indexOf(String(lot.product_id)) < 0)) {
      this.snackBar.open('El producto del lote debe estar incluido en la orden de trabajo.', 'Cerrar', {duration: 4000}); return false;
    }
    return true;
  }

  private prepareLoadedOrder(order: any): void {
    const creator = order.CreatedByOperator || order.created_by_operator || order.Creator || order.creator;
    this.creatorId = order.created_by_operator_id || order.create_uid || (this.user && this.user.id);
    this.creatorName = creator && (creator.fullname || creator.name || creator.login) || this.creatorName;
    order.creator_label = this.getCreatorLabel(); order.source_type = 'MANUAL';
    order.planned_start = this.toInputDate(order.planned_start); order.planned_end = this.toInputDate(order.planned_end);
    const sourceProducts = order.Products || order.products || [];
    order.products = sourceProducts.map(product => ({
      id: product.id,
      product_id: product.product_id || (product.Product && product.Product.id),
      planned_units: product.planned_units !== null && product.planned_units !== undefined
        ? Number(product.planned_units) : null
    }));
    // Compatibilidad de lectura mientras existan órdenes con el contrato antiguo.
    if (!order.products.length && order.product_id) {
      order.products.push({product_id: order.product_id, planned_units: order.planned_units});
    }
    const formProducts = this.itemForm.get('products') as FormArray;
    while (formProducts.length) formProducts.removeAt(0);
    order.products.forEach(product => formProducts.push(this.createProductRow(product)));
    this.syncProductDisplay();
    const sourceLots = order.Lots || order.lots || order.WorkOrderLots || order.work_order_lots || [];
    order.lots = sourceLots.map(lot => ({id: lot.id, lot_number: lot.lot_number, product_id: lot.product_id,
      status_id: lot.status_id, source_reference: lot.source_reference || ''}));
    this.loadedLotIds = order.lots.filter(lot => lot.id).map(lot => Number(lot.id));
    this.collectStatuses([order]); this.configureOptions();
    const formLots = this.itemForm.get('lots') as FormArray;
    while (formLots.length) formLots.removeAt(0);
    order.lots.forEach(lot => formLots.push(this.createLotRow(lot)));
    this.syncLotDisplay();
    const requestedLotId = Number(this.route.snapshot.queryParamMap.get('lot_id'));
    this.getInput('lots').editingRowId = requestedLotId > 0 ? requestedLotId : null;
  }

  private createLotRow(lot: any): FormGroup {
    return new FormGroup({id: new FormControl(lot.id), lot_number: new FormControl(lot.lot_number),
      product_id: new FormControl(lot.product_id), status_id: new FormControl(lot.status_id),
      source_reference: new FormControl(lot.source_reference || '')});
  }

  private createProductRow(product: any): FormGroup {
    return new FormGroup({
      id: new FormControl(product.id),
      product_id: new FormControl(product.product_id, Validators.required),
      planned_units: new FormControl(product.planned_units, [Validators.required, Validators.min(0)])
    });
  }

  private configureOptions(): void {
    const products = this.data.products.map(product => ({text: product.name, value: product.id}));
    const statuses = this.data.statuses.map(status => ({text: status.name, value: status.id}));
    this.getInput('status_id').optionList = statuses;
    this.getInput('products').columnList.find(column => column.name === 'product_id').optionList = products;
    const columns = this.getInput('lots').columnList;
    columns.find(column => column.name === 'status_id').optionList = statuses;
    this.configureLotProductOptions();
    if (!this.itemForm.get('status_id').value && statuses.length) this.itemForm.get('status_id').setValue(statuses[0].value);
  }

  private configureLotProductOptions(): void {
    const selectedIds = ((this.itemForm.get('products') as FormArray).value || [])
      .map(product => product.product_id).filter(productId => productId !== null && productId !== '');
    this.getInput('lots').columnList.find(column => column.name === 'product_id').optionList = selectedIds.map(id => ({
      value: id,
      text: this.getProductName(id)
    }));
  }

  private syncProductDisplay(): void {
    const products = (this.itemForm.get('products') as FormArray).value || [];
    this.getInput('products').selected = products.map(product => [
      this.getProductName(product.product_id),
      product.planned_units
    ]);
    this.configureLotProductOptions();
  }

  private syncLotDisplay(): void {
    const lots = (this.itemForm.get('lots') as FormArray).value || [];
    const orderStatusId = this.itemForm.get('status_id').value;
    this.getInput('lots').selected = lots.map(lot => [
      lot.lot_number,
      this.getProductName(lot.product_id),
      this.getStatusName(lot.status_id || orderStatusId),
      lot.source_reference || '-'
    ]);
  }

  private collectStatuses(orders: any[]): void {
    const byId: {[key: string]: any} = {};
    this.data.statuses.forEach(status => byId[String(status.id)] = status);
    orders.forEach(order => {
      if (!order || order.status_id == null) return;
      const status = order.Status || order.WorkOrderStatus || order.status;
      byId[String(order.status_id)] = {id: order.status_id, name: status && status.name || order.status_name || 'Estado ' + order.status_id};
    });
    this.data.statuses = Object.keys(byId).map(key => byId[key]);
  }

  private clearCompanyData(): void {
    this.data.products = [];
    this.clearRows('products');
    this.getInput('products').selected = [];
    this.clearRows('lots');
    this.getInput('lots').selected = []; this.loadedLotIds = [];
  }

  private clearRows(name: string): void {
    const rows = this.itemForm.get(name) as FormArray;
    while (rows.length) rows.removeAt(0);
  }

  private clone(value: any): any {
    return JSON.parse(JSON.stringify(value));
  }

  private toInputDate(value: any): string {
    if (!value) return null;
    const date = new Date(value); const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }
}
